import { Box, Icon, Stack } from '@mui/material'
import TextField from '@mui/material/TextField'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdHighlightOff } from 'react-icons/md'
import { CloseButton, ColumnOptionInput } from './GenericTable.style'

export interface ColumnSelectOption {
  field: string
  label: string
}

interface ColumnOptionRowProps {
  rowId: string
  field: string
  headerName: string
  isColumnOption: boolean
  /** Fields user can pick from (excludes actions, etc.) */
  selectOptions: ColumnSelectOption[]
  actionsLabel: string
  onFieldChange: (previousField: string, newField: string) => void
  onRemove: (field: string) => void
}

export const ColumnOptionRow: React.FC<ColumnOptionRowProps> = ({
  rowId,
  field,
  headerName,
  isColumnOption,
  selectOptions,
  actionsLabel,
  onFieldChange,
  onRemove,
}) => {
  const { t } = useTranslation(['common', 'generic-table'])

  const valueOption: ColumnSelectOption = useMemo(() => {
    if (!field) {
      return { field: '', label: actionsLabel }
    }
    const fromList = selectOptions.find(o => o.field === field)
    if (fromList) return fromList
    const fallbackLabel = field === 'actions' ? t(field) : headerName || field
    return { field, label: fallbackLabel }
  }, [field, selectOptions, actionsLabel, headerName, t])

  const optionsWithCurrent = useMemo(() => {
    if (!field || selectOptions.some(o => o.field === field)) {
      return selectOptions
    }
    return [...selectOptions, valueOption]
  }, [field, selectOptions, valueOption])

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      width="100%"
      height="40px"
    >
      <Box height="100%" width="100%">
        <ColumnOptionInput
          disablePortal
          id={`column-option-${rowId}`}
          options={optionsWithCurrent}
          getOptionLabel={o => {
            const opt = o as ColumnSelectOption
            return opt.label ?? opt.field ?? ''
          }}
          isOptionEqualToValue={(a, b) =>
            (a as ColumnSelectOption).field === (b as ColumnSelectOption).field
          }
          value={valueOption}
          onChange={(_event, v) => {
            const next = v as ColumnSelectOption | null
            if (next != null && isColumnOption && next.field !== field) {
              onFieldChange(field, next.field)
            }
          }}
          sx={{ width: '100%', height: '20px' }}
          renderInput={params => <TextField {...params} label="" />}
          disabled={!isColumnOption}
        />
      </Box>
      <Box width={isColumnOption ? 'auto' : '48px'}>
        {isColumnOption && (
          <CloseButton type="button" onClick={() => onRemove(field)} aria-label="Remove column">
            <Icon>
              <MdHighlightOff />
            </Icon>
          </CloseButton>
        )}
      </Box>
    </Stack>
  )
}
